/**
 * Silent retry on chunk-load failure (2 attempts, 1s/4s backoff, cache-busted re-fetch) to
 * recover from transient mobile-network errors. See doc/plans/PRNT-4319-chunk-load-retry.md for
 * the original analysis and doc/plans/chunk-retry-backoff.md for the current retry behavior.
 *
 * `loadable()` in `@shared/core/loadableFromNamedExport` (and its `.lib` variant) applies it for you.
 * Use `importWithRetry` for a bare `import()` that is not behind a loadable.
 *
 * Kept free of i18n and @loadable/component so browser bootstrap code (static/js) can use it
 * without pulling those in.
 *
 * @module @shared/core/chunkRetry
 */

import globalWindow from '@shared/core/globals';
import { isChunkLoadError as isChunkLoadErrorMessage } from '@shared/utils/isChunkLoadError';
import { addAction } from '@shared/utils/logger';
import { isPageUnloading } from '@shared/utils/pageUnloading';

// Number of silent retry attempts after the original load fails. Attempt 1 alone recovers
// ~28% of failures in prod; NR shows the remaining exhaustion pool skews to flaky-but-alive
// connections (timeouts on 3g/4g, page visible, navigator.onLine true), and 65% of sessions
// that hit any chunk failure hit 2+ — i.e. a single 1s retry usually fires while the network
// is still down. A second, later attempt targets exactly that population.
const MAX_RETRIES = 2;
// Delay before each attempt, indexed by `attempt - 1`. Exponential-ish rather than uniform:
// attempt 1 stays at 1s (fast recovery for a momentary blip, unchanged from PRNT-4319),
// attempt 2 waits 4s to give a recovering radio time to come back.
export const RETRY_DELAYS_MS = [1000, 4000];
// Ceiling on a single retry ATTEMPT as a whole — the injected `<script>` and the `loader()`
// call share it rather than each getting their own, since they run back to back and a slow but
// successful script does not throw. Capping them independently would let one attempt reach ~30s
// and the sequence ~65s. Webpack 4's default `chunkLoadTimeout` is 120s, far too long once the
// original load has already failed.
//
// A flat per-attempt cap rather than a total-sequence budget, deliberately. The worst case is
// bounded by construction: 1s + 15s + 4s + 15s = 35s of loading fallback before the error
// boundary takes over. A running budget bought a slightly tighter 40s but needed arithmetic at
// four checkpoints, and every one of those checkpoints was a bug at some point (unbounded
// loader, backoff overshoot, an unset flag, and a negative timeout when a suspended tab
// stretched the sleep). Simple first; revisit with data if the cap turns out to be wrong.
//
// 15s is ~7x the measured need: the worst-offender chunks transfer in ~2.1s on Slow 3G with
// real production brotli bytes. The previous 30s predates that measurement.
const RETRY_ATTEMPT_TIMEOUT_MS = 15000;

// The detector the error boundaries use, narrowed to `unknown` inputs. Shared rather than
// re-implemented: the two drifting is exactly what let CSS failures reach the section boundary
// without a retry.
export const isChunkLoadError = (err: unknown): boolean => !!err && typeof err === 'object' && isChunkLoadErrorMessage(err as Error);

/** `new URL` that yields `undefined` instead of throwing on a URL it cannot parse. */
const parseChunkUrl = (url: string): URL | undefined => {
  try {
    return new URL(url, globalWindow.location?.href);
  } catch {
    return undefined;
  }
};

// Both chunk runtimes attach the failed URL as `err.request` — webpack's JSONP runtime
// (`error.request = realSrc`) and CssExtractRspackPlugin's stylesheet runtime
// (`err.request = realHref`). We use it to re-load the chunk with a cache-busting query param
// without needing webpack-internal chunk-URL builders.
const extractFailedChunkUrl = (err: unknown): string | undefined => {
  const request = (err as { request?: unknown } | null | undefined)?.request;
  if (typeof request === 'string') return request;
  // Both runtimes also put the URL in the message — `Loading chunk 42 failed.\n(error:
  // https://…/42.abc.js)` for JS, `Loading CSS chunk 33011 failed.\n(https://…/33011.abc.css)`
  // for CSS (no `errorType:` prefix there). Since both set `request`, this only matters when the
  // error crossed a boundary that dropped the non-standard property; without it such a failure
  // has no URL to cache-bust and falls through to a bare loader() retry of the same href.
  const message = String((err as { message?: unknown } | null | undefined)?.message ?? '');
  return message.match(/\(\s*(?:[a-z]+:\s+)?((?:https?:\/\/|\/)[^)\s]+?)\s*\)/i)?.[1];
};

const isCssChunkUrl = (url: string): boolean => {
  const parsed = parseChunkUrl(url);
  return parsed ? parsed.pathname.endsWith('.css') : /\.css(?:[?#]|$)/.test(url);
};

/**
 * The value CssExtract's `findStylesheet` compares against, so the next `loader()` call reuses
 * the retry `<link>` instead of firing a second request for the href that just failed.
 *
 * `findStylesheet` takes `data-href || href`, strips the query, and tests it against `href`
 * (`miniCssF(chunkId)`) or `fullhref` (`output.publicPath + href`). So the value to hand it is
 * `fullhref` — which is reconstructed, not copied: in production this build's `publicPath` is
 * path-absolute (`/bliss-apps/joy-web/web/`, see `config/webpack/utils.ts`) while the failed
 * URL arrives fully resolved off `event.target.href`, and the absolute form matches neither
 * candidate. Dropping the origin when it is the document's own reproduces `fullhref` exactly;
 * keeping it otherwise covers dev, where assets come from another port and `publicPath` IS
 * absolute. The query goes in both cases — `publicPath` never carries one.
 */
const stylesheetLookupHref = (failedUrl: string): string => {
  const parsed = parseChunkUrl(failedUrl);
  if (!parsed) return failedUrl;
  return parsed.origin === globalWindow.location?.origin ? parsed.pathname : parsed.origin + parsed.pathname;
};

/**
 * Set `key` on a URL's query string, replacing any existing occurrence rather than appending a
 * second one — with more than one retry attempt the URL handed to attempt 2 can already carry
 * `?retry=1`, and `?retry=1&retry=2` still cache-busts but splits one logical chunk across two
 * URL shapes in CDN logs and NR facets.
 *
 * Uses `URL`/`searchParams` rather than string surgery so fragments, a stray trailing `?` and
 * separator placement are the platform's problem, not ours. Resolved against
 * `location.href` because `webPublicPath` is path-absolute (`/bliss-apps/joy-web/web/`), so
 * `err.request` can arrive without a host — the same base `normalizeChunkUrl` uses.
 *
 * Absolutizing a host-less input is harmless: the browser would resolve it against the same
 * origin anyway, and this only ever feeds a `<script src>` or `<link href>`.
 *
 * Falls back to the original URL if parsing fails, so a malformed `err.request` still gets a
 * retry (just without the cache-bust) rather than throwing a `TypeError` out of the retry loop,
 * where it would be misclassified as a broken module and propagated instead of retried.
 */
export const withQueryParam = (url: string, key: string, value: string | number): string => {
  const parsed = parseChunkUrl(url);
  if (!parsed) return url;
  parsed.searchParams.set(key, String(value));
  return parsed.href;
};
/**
 * Normalize a chunk URL into host + path + filename for NR faceting.
 * Avoids logging full URLs (which include content-hashed filenames that change per deploy).
 */
const normalizeChunkUrl = (url: string): { failedUrlHost: string; failedUrlPath: string } => {
  const parsed = parseChunkUrl(url);
  if (!parsed) return { failedUrlHost: 'unknown', failedUrlPath: url };
  const pathParts = parsed.pathname.split('/');
  const filename = pathParts.pop() ?? '';
  // Strip the content hash: "registryList.b09dfb3e.js" → "registryList.js" (same for .css)
  const normalized = filename.replace(/\.[a-f0-9]{8}\.(js|css)$/, '.$1');
  return {
    failedUrlHost: parsed.host,
    failedUrlPath: pathParts.join('/') + '/' + normalized,
  };
};

/** Snapshot client environment at the moment of failure for NR classification. */
const captureClientContext = (): Record<string, string | number | boolean | undefined> => {
  const nav = globalWindow.navigator as (Navigator & { connection?: { effectiveType?: string; saveData?: boolean } }) | undefined;
  return {
    online: nav?.onLine,
    visibilityState: globalWindow.document?.visibilityState,
    connectionEffectiveType: nav?.connection?.effectiveType,
    connectionSaveData: nav?.connection?.saveData,
    pageUnloading: isPageUnloading(),
  };
};

/**
 * Extract a low-cardinality error reason from a chunk-load error message.
 * Raw messages contain full URLs with content hashes (deploy churn), so we
 * classify into a small set of reason strings instead of logging the raw text.
 */
const classifyChunkErrorReason = (err: unknown): string => {
  const message = String((err as { message?: string }).message ?? '');
  if (/timeout/i.test(message)) return 'timeout';
  if (/missing/i.test(message)) return 'missing';
  // CSS before the generic branches: CssExtractRspackPlugin leaves `name` unset, so without
  // this the two runtimes' failures would land in the same `chunk-load-error` bucket.
  // Case-insensitive to match `isChunkLoadError`, which decides whether we retry at all — a
  // message it accepts but this rejects would be retried and then faceted as `unknown`.
  if (/loading css chunk/i.test(message)) return 'css-chunk-load-error';
  if ((err as { name?: string }).name === 'ChunkLoadError') return 'chunk-load-error';
  if (/loading chunk/i.test(message)) return 'chunk-load-error';
  return 'unknown';
};

// Manually inject the failed chunk with a cache-bust query param.
//
// JS: a `<script>`. When it loads, webpack's JSONP callback
// (`window["__LOADABLE_LOADED_CHUNKS__"].push(...)`) runs from inside the chunk's bootstrap
// code, which marks `installedChunks[chunkId] = 0` and registers all module factories — so a
// subsequent call to the original `loader()` resolves immediately without a second network
// request.
//
// CSS: a `<link rel="stylesheet">`. CssExtractRspackPlugin runs no JSONP; its stylesheet
// runtime looks for an existing tag whose `data-href`/`href` matches the chunk URL it is about
// to request (`findStylesheet`). We point `data-href` at that URL — see `stylesheetLookupHref`
// for why it is not simply `failedUrl` — so `loader()` reuses this tag instead of firing a
// second request for the href that just failed.
const loadChunkWithCacheBust = (failedUrl: string, attempt: number, timeoutMs: number): Promise<void> =>
  new Promise((resolve, reject) => {
    // `document` is the canonical reference here. SSR never reaches this path (async chunks
    // load synchronously via `requireSync` on the server), but guard defensively so an
    // accidental SSR call fails loudly instead of crashing with "document is not defined".
    // Checked BEFORE building the URL: without a document there is no `location` to resolve a
    // host-less `err.request` against.
    if (typeof document === 'undefined') {
      reject(new Error('Chunk retry attempted without a DOM'));
      return;
    }
    const url = withQueryParam(failedUrl, 'retry', attempt);
    const isCss = isCssChunkUrl(failedUrl);
    const el: HTMLScriptElement | HTMLLinkElement = isCss ? document.createElement('link') : document.createElement('script');
    if (isCss) {
      const link = el as HTMLLinkElement;
      link.rel = 'stylesheet';
      link.href = url;
      link.setAttribute('data-href', stylesheetLookupHref(failedUrl));
      // Deliberately no `crossOrigin`: `output.crossOriginLoading` is unset, so the stylesheet
      // links the bundler itself emits carry no `crossorigin`. Adding it here would make the
      // retry a CORS request and fail on any asset host that does not send `Access-Control-
      // Allow-Origin` — a fetch the original, working request never had to survive.
    } else {
      const script = el as HTMLScriptElement;
      script.src = url;
      script.async = true;
      script.crossOrigin = 'anonymous';
    }
    const kind = isCss ? 'css' : 'script';
    let timeoutId: ReturnType<typeof globalWindow.setTimeout> | undefined;
    const cleanup = () => {
      el.onload = null;
      el.onerror = null;
      if (timeoutId !== undefined) {
        globalWindow.clearTimeout(timeoutId);
        timeoutId = undefined;
      }
    };
    el.onload = () => {
      cleanup();
      resolve();
    };
    el.onerror = () => {
      cleanup();
      el.remove();
      reject(new Error(`Chunk retry ${kind} failed: ${url}`));
    };
    // Without this, a stalled response (the 21% timeout-class failures we're meant to
    // recover) would leave the wrapper awaiting `onload`/`onerror` forever and the lazy
    // route stuck on its fallback. Mirrors webpack's own chunk-load-timeout behavior.
    timeoutId = globalWindow.setTimeout(() => {
      cleanup();
      el.remove();
      reject(new Error(`Chunk retry ${kind} timed out: ${url}`));
    }, timeoutMs);
    document.head.appendChild(el);
  });

/**
 * Classify how a retry attempt failed, for NR faceting on the Exhausted event. Distinguishes
 * "we never got the bytes" (script/css-error / script/css-timeout — a network problem, which
 * more backoff could plausibly fix) from "we got the bytes and the loader still rejected"
 * (loader-error — retrying is pointless, the chunk or its dependency graph is broken).
 */
type RetryFailure = 'script-error' | 'script-timeout' | 'css-error' | 'css-timeout' | 'loader-error' | 'loader-timeout';

const classifyRetryFailure = (err: unknown): RetryFailure => {
  const message = String((err as { message?: string } | null | undefined)?.message ?? '');
  if (message.startsWith('Chunk retry css timed out')) return 'css-timeout';
  if (message.startsWith('Chunk retry css failed')) return 'css-error';
  if (message.startsWith('Chunk retry script timed out')) return 'script-timeout';
  if (message.startsWith('Chunk retry script failed')) return 'script-error';
  if (message.startsWith('Chunk retry loader timed out')) return 'loader-timeout';
  return 'loader-error';
};

/**
 * Reject with `Chunk retry loader timed out` if `promise` has not settled within `ms`.
 *
 * The abandoned promise keeps running — a webpack chunk request cannot be cancelled — but
 * nothing consumes its result, exactly as an abandoned retry `<script>` is left to finish
 * after being removed from the DOM.
 */
const withDeadline = <T>(promise: Promise<T>, ms: number): Promise<T> =>
  new Promise<T>((resolve, reject) => {
    const timeoutId = globalWindow.setTimeout(() => reject(new Error(`Chunk retry loader timed out after ${ms}ms`)), ms);
    promise.then(
      value => {
        globalWindow.clearTimeout(timeoutId);
        resolve(value);
      },
      error => {
        globalWindow.clearTimeout(timeoutId);
        reject(error);
      },
    );
  });

export const delay = (ms: number): Promise<void> => new Promise(resolve => globalWindow.setTimeout(resolve, ms));

// Wraps the chunk-load promise with retry. Orthogonal to `resolveComponent` (which runs
// AFTER the chunk loads successfully). The `chunkName` (when known) is sent with telemetry
// and lets us facet retry recovery rates by chunk in New Relic.
//
// Implemented as a flat loop rather than recursion on purpose. The recursive shape this
// replaced re-entered the whole wrapper per attempt, which made two things structurally
// impossible to get right once MAX_RETRIES exceeded 1:
//   1. a failed cache-bust `<script>` had no way to reach the next attempt — it had to emit
//      Exhausted and reject from inside the retry, so the only path to attempt 2 was
//      "script loaded but loader still rejected" (rare). The common case — dead radio, script
//      `onerror` — silently kept its single-attempt behavior.
//   2. every frame on the way out emitted its own ChunkRetrySuccess, so one recovery on
//      attempt 2 double-counted as attempt=2 AND attempt=1 in NR.
// One loop, one terminal event per load: exactly one of ChunkRetrySuccess or
// ChunkRetryExhausted is emitted per wrapped call that entered retry.
export const withChunkRetry =
  <T>(loader: () => Promise<T>, chunkName?: string): (() => Promise<T>) =>
  async (): Promise<T> => {
    let originalErr: unknown;
    try {
      return await loader();
    } catch (err) {
      // Anything that isn't a chunk-load failure (a module that throws at evaluation time, a
      // rejected import of a genuinely missing export) is not a network problem — surface it
      // untouched and without telemetry, exactly as the pre-retry code did.
      if (!isChunkLoadError(err)) throw err;
      originalErr = err;
    }

    const failedUrl = extractFailedChunkUrl(originalErr);
    const urlParts = failedUrl ? normalizeChunkUrl(failedUrl) : {};
    // Facets describing the ORIGINAL failure. Held constant across attempts so every event
    // for one logical load groups under the same chunk/reason in NR.
    const facets = {
      chunkName,
      hasRequest: Boolean(failedUrl),
      ...urlParts,
      errorName: (originalErr as { name?: string }).name,
      errorReason: classifyChunkErrorReason(originalErr),
    };
    // The chunk to cache-bust next. Starts at the original failure but is RETARGETED whenever
    // a later attempt's `loader()` names a different chunk: one loadable import can require
    // several split chunks, so attempt 1 can fix chunk A and then fail on chunk B. Leaving
    // this pinned would re-inject A forever and request B without a cache-bust, letting a bad
    // cached response for B exhaust the retries. Deliberately separate from `facets`, which
    // stays pinned to the original failure so all events for one load group together in NR.
    let activeFailedUrl = failedUrl;
    const sequenceStartMs = Date.now();
    // How the final attempt failed, and how many we actually made. Counted rather than assumed
    // so an Exhausted event cannot claim 2 attempts when only 1 ran.
    let lastFailure: RetryFailure | 'none' = 'none';
    let attemptsMade = 0;

    for (let attempt = 1; attempt <= MAX_RETRIES; attempt++) {
      // Captured BEFORE the sleep: the pre-backoff implementation timed from here too, so
      // `ChunkRetrySuccess.elapsedMs` keeps covering "backoff + fetch". Starting the clock
      // after the sleep would silently drop 1s (attempt 1) or 4s (attempt 2), showing existing
      // latency dashboards an artificial improvement on deploy.
      const attemptStartMs = Date.now();
      await delay(RETRY_DELAYS_MS[attempt - 1]);

      attemptsMade = attempt;
      addAction('ChunkRetryAttempt', {
        ...facets,
        attempt,
        cacheBust: Boolean(activeFailedUrl),
        ...captureClientContext(),
      });

      try {
        // If we have the failed URL, pre-load the chunk with a cache-bust query — this
        // recovers from CDN-edge-cache corruption (the Sydney pattern). When the script
        // loads, webpack's JSONP callback marks the chunk installed, so the loader() call
        // below resolves immediately from cache without a second network round-trip.
        // If the script itself fails we skip loader() for this attempt (it would only repeat
        // the request that just failed) and fall through to the next attempt, if any.
        // The cap covers the attempt as a WHOLE, so the script and the loader share it rather
        // than each getting a fresh 15s. They run back to back, and a script that succeeds
        // slowly does not throw — so independent caps let one attempt reach ~30s and the
        // sequence ~65s, the exact figure this cap exists to rule out.
        const operationStartMs = Date.now();
        if (activeFailedUrl) {
          await loadChunkWithCacheBust(activeFailedUrl, attempt, RETRY_ATTEMPT_TIMEOUT_MS);
        }
        // Bound `loader()` too, not just the injected script. When an import needs several
        // split chunks, the script can repair the first one and `loader()` can then sit pending
        // on another for webpack's 120s `chunkLoadTimeout`, parking the user on the loading
        // fallback long past the point the error boundary should have taken over.
        // `Math.max(0, …)` because a suspended tab can overrun the script phase: a negative
        // deadline would fire immediately and open-then-abandon the request in one tick.
        const remainingAttemptMs = Math.max(0, RETRY_ATTEMPT_TIMEOUT_MS - (Date.now() - operationStartMs));
        const result = await withDeadline(loader(), remainingAttemptMs);
        addAction('ChunkRetrySuccess', {
          ...facets,
          attempt,
          // Time spent on the winning attempt (matches the pre-backoff metric definition)
          // alongside total time the user waited across the whole sequence.
          elapsedMs: Date.now() - attemptStartMs,
          totalElapsedMs: Date.now() - sequenceStartMs,
          ...captureClientContext(),
        });
        return result;
      } catch (retryErr) {
        // A non-chunk rejection out of loader() means the bytes arrived and the module itself
        // is broken. More attempts can't fix that, and swallowing it into the original
        // ChunkLoadError would hide the real cause — propagate it as-is.
        if (!isChunkLoadError(retryErr) && classifyRetryFailure(retryErr) === 'loader-error') throw retryErr;
        lastFailure = classifyRetryFailure(retryErr);
        // Retarget at whatever chunk this attempt actually failed on. Script failures carry no
        // `request`, so those leave the current target in place rather than clearing it.
        const nextFailedUrl = extractFailedChunkUrl(retryErr);
        if (nextFailedUrl) activeFailedUrl = nextFailedUrl;
      }
    }

    const totalElapsedMs = Date.now() - sequenceStartMs;
    addAction('ChunkRetryExhausted', {
      ...facets,
      attempts: attemptsMade,
      elapsedMs: totalElapsedMs,
      lastFailure,
      ...captureClientContext(),
    });
    // Propagate the ORIGINAL chunk error, not the internal "Chunk retry script failed"
    // wrapper — error boundaries and NR error grouping expect a ChunkLoadError here.
    throw originalErr;
  };

/**
 * Run a bare dynamic import with the same silent retry `loadable()` gets.
 *
 * Keep the `import()` (and its `webpackChunkName` comment) inside the thunk so the chunk name is
 * unchanged. Final failure rejects with the original error, so existing `.catch` handlers still
 * see what they saw before, just after the retries.
 */
export const importWithRetry = <T>(loader: () => Promise<T>, chunkName?: string): Promise<T> => withChunkRetry(loader, chunkName)();
